/**
 * Tool Handlers
 * Actual execution logic for each AI tool.
 * These functions call the appropriate services/APIs and return results.
 */

import { useAppContext } from '../contexts/Storage';
import { dashboardService } from '../services/dashboardService';
import { recordsService } from '../services/recordsService';
import { transactionsService } from '../services/transactionsService';
import { transactionEntitiesService } from '../services/transactionEntitiesService';
import { accountsService } from '../services/accountsService';
import { creditPointsService } from '../services/creditPointsService';
import { subscriptionService } from '../services/subscriptionService';
import { notificationsService } from '../services/notificationsService';

export interface ToolResult {
  success: boolean;
  data: unknown;
  formattedMessage?: string;
  error?: string;
}

/**
 * Parse the arguments from a tool call
 */
function parseArgs(args: string): Record<string, unknown> {
  try {
    return JSON.parse(args);
  } catch {
    return {};
  }
}

/**
 * Get all tool handlers.
 * This is a factory function that receives the app context to use for service calls.
 */
export function createToolHandlers(appContext: ReturnType<typeof useAppContext>) {
  const { token, user } = appContext;

  if (!token) {
    throw new Error('Authentication token not available');
  }

  const userId = user?.id;
  if (!userId) {
    throw new Error('User not identified');
  }

  return {
    // ============ READ TOOLS ============

    get_dashboard_kpis: async (args: string): Promise<ToolResult> => {
      try {
        const result = await dashboardService.getDashboardKpis(userId, token);
        if (!result.success) throw new Error(result.error || 'Failed to fetch KPIs');
        return {
          success: true,
          data: result.data,
          formattedMessage: `Dashboard KPIs retrieved. Here's the data:\n${JSON.stringify(result.data, null, 2)}`,
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    get_records: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const filter: any = { user_id: userId };
        if (params.record_id !== undefined) filter.id = Number(params.record_id);
        const result = await recordsService.getRecords(token, filter);
        if (!result.success) throw new Error(result.error || 'Failed to fetch records');
        const records = result.data || [];
        return {
          success: true,
          data: records,
          formattedMessage: records.length > 0
            ? `Found ${records.length} record(s):\n${records.map((r: any) => `- **${r.record_name}** (ID: ${r.id})${r.record_currency_code ? ` - ${r.record_currency_code}` : ''}`).join('\n')}`
            : 'No records found. You can create one!',
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    get_transactions: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const filter: any = { record_id: params.record_id };
        if (params.start_date) filter.start_date = params.start_date as string;
        if (params.end_date) filter.end_date = params.end_date as string;
        if (params.transaction_action_id) filter.transaction_action_id = Number(params.transaction_action_id);
        if (params.transaction_object_id) filter.transaction_object_id = Number(params.transaction_object_id);
        if (params.transaction_media_id) filter.transaction_media_id = Number(params.transaction_media_id);
        if (params.amount_min) filter.amount_min = Number(params.amount_min);
        if (params.amount_max) filter.amount_max = Number(params.amount_max);
        if (params.quantity_min) filter.quantity_min = Number(params.quantity_min);
        if (params.quantity_max) filter.quantity_max = Number(params.quantity_max);
        if (params.page) filter.page = Number(params.page);
        if (params.limit) filter.limit = Number(params.limit);

        const result = await transactionsService.getTransactions(token, userId, filter);
        if (!result.success) throw new Error(result.error || 'Failed to fetch transactions');
        const data = result.data as any;
        const transactions = data?.transactions?.data || [];
        return {
          success: true,
          data: transactions,
          formattedMessage: transactions.length > 0
            ? `Found ${transactions.length} transaction(s):\n${transactions.map((t: any) => {
              const action = t.action?.transaction_entity_name || '?';
              const object = t.object?.transaction_entity_name || '?';
              const media = t.media?.transaction_entity_name || '?';
              return `- ${t.transaction_date}: ${action} ${object} (Qty: ${t.transaction_quantity}, Amount: ${t.transaction_total_amount}, Media: ${media})`;
            }).join('\n')}`
            : 'No transactions found matching the criteria.',
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    get_transaction_entities: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const body: any = {};
        if (params.category) body.category = params.category as string;
        if (params.name) body.name = params.name as string;
        if (params.type) body.type = params.type as string;
        if (params.linked_to_kpi !== undefined) body.linked_to_kpi = Boolean(params.linked_to_kpi);
        if (params.record_id !== undefined) body.record_id = Number(params.record_id);
        if (params.id !== undefined) body.id = Number(params.id);
        if (params.page) body.page = Number(params.page);
        if (params.limit) body.limit = Number(params.limit);
        const result = await transactionEntitiesService.findTransactionEntities(token, userId, body);
        if (!result.success) throw new Error(result.error || 'Failed to fetch entities');
        const entities = result.data?.data || [];
        return {
          success: true,
          data: entities,
          formattedMessage: entities.length > 0
            ? `Found ${entities.length} entity/entities:\n${entities.map((e: any) => `- **${e.transaction_entity_name}** (ID: ${e.id}, Category: ${e.transaction_entity_category})${e.transaction_entity_type ? `, Type: ${e.transaction_entity_type}` : ''}`).join('\n')}`
            : 'No entities found.',
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    get_ledgers: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const ledgerFilter: any = { record_id: Number(params.record_id), user_id: userId };
        if (params.start_date) ledgerFilter.start_date = params.start_date as string;
        if (params.end_date) ledgerFilter.end_date = params.end_date as string;
        if (params.page) ledgerFilter.page = Number(params.page);
        const result = await accountsService.fetchLedgerAccounts(token, ledgerFilter);
        if (!result.success) throw new Error(result.error || 'Failed to fetch ledgers');
        const data = result.data as any;
        const accounts = data?.accounts || data?.data || [];
        return {
          success: true,
          data: accounts,
          formattedMessage: accounts.length > 0
            ? `Ledger Accounts:\n${accounts.map((a: any) => {
              const name = a.transaction_entity_name || a.name || 'Unknown';
              const balance = a.transaction_entity_balance_amount || a.balance || 0;
              const type = a.transaction_entity_balance_type || a.type || '';
              return `- **${name}**: Balance ${balance} ${type ? `(${type})` : ''}`;
            }).join('\n')}`
            : 'No ledger accounts found.',
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    get_ledger_detail: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const result = await accountsService.fetchLedgerDetail(token, {
          entity_id: Number(params.entity_id),
          record_id: Number(params.record_id),
          user_id: userId,
          start_date: params.start_date as string,
          end_date: params.end_date as string,
        } as any);
        if (!result.success) throw new Error(result.error || 'Failed to fetch ledger detail');
        return { success: true, data: result.data, formattedMessage: `Ledger detail retrieved successfully.` };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    get_balance_sheet: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const result = await accountsService.fetchBalanceSheet(token, {
          record_id: Number(params.record_id),
          user_id: userId,
          start_date: params.start_date as string,
          end_date: params.end_date as string,
        } as any);
        if (!result.success) throw new Error(result.error || 'Failed to fetch balance sheet');
        const data = result.data as any;
        return {
          success: true,
          data,
          formattedMessage: data
            ? `Balance Sheet (${params.start_date} to ${params.end_date}):\n\n**Assets:**\n${formatObjectForDisplay(data.assets)}\n\n**Liabilities:**\n${formatObjectForDisplay(data.liabilities)}\n\n**Capital:**\n${formatObjectForDisplay(data.capital)}`
            : 'No balance sheet data available.',
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    get_profit_loss: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const result = await accountsService.fetchProfitAndLoss(token, {
          record_id: Number(params.record_id),
          user_id: userId,
          start_date: params.start_date as string,
          end_date: params.end_date as string,
        } as any);
        if (!result.success) throw new Error(result.error || 'Failed to fetch P&L');
        const data = result.data as any;
        return {
          success: true,
          data,
          formattedMessage: data
            ? `Profit & Loss (${params.start_date} to ${params.end_date}):\n\n**Debits (Expenses):**\n${formatObjectForDisplay(data.debit)}\n**Total Debits:** ${data.total_debit}\n\n**Credits (Revenues):**\n${formatObjectForDisplay(data.credit)}\n**Total Credits:** ${data.total_credit}\n\n**Net ${(data.total_credit || 0) >= (data.total_debit || 0) ? 'Profit' : 'Loss'}:** ${Math.abs((data.total_credit || 0) - (data.total_debit || 0))}`
            : 'No P&L data available.',
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    get_stock_valuation: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const svFilter: any = { record_id: Number(params.record_id), user_id: userId };
        if (params.page) svFilter.page = Number(params.page);
        const result = await accountsService.fetchStockValuationAccounts(token, svFilter);
        if (!result.success) throw new Error(result.error || 'Failed to fetch stock valuation');
        const data = result.data as any;
        const stocks = data?.accounts || data?.data || [];
        return {
          success: true,
          data: stocks,
          formattedMessage: stocks.length > 0
            ? `Stock Valuation Accounts:\n${stocks.map((s: any) => `- **${s.transaction_entity_name}**: Qty ${s.transaction_entity_balance_quantity || 0}, Amount ${s.transaction_entity_balance_amount || 0}`).join('\n')}`
            : 'No stock valuation accounts found.',
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    get_trading_accounts: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const taFilter: any = { record_id: Number(params.record_id), user_id: userId };
        if (params.page) taFilter.page = Number(params.page);
        const result = await accountsService.fetchTradingAccounts(token, taFilter);
        if (!result.success) throw new Error(result.error || 'Failed to fetch trading accounts');
        const data = result.data as any;
        const accounts = data?.accounts || data?.data || [];
        return {
          success: true,
          data: accounts,
          formattedMessage: accounts.length > 0
            ? `Trading Accounts:\n${accounts.map((a: any) => `- **${a.transaction_entity_name}**: Balance ${a.transaction_entity_balance_amount || 0}`).join('\n')}`
            : 'No trading accounts found.',
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    get_credit_balance: async (): Promise<ToolResult> => {
      try {
        const result = await creditPointsService.getBalance(token, userId);
        if (!result.success) throw new Error(result.error || 'Failed to fetch credit balance');
        const balance = result.data?.balance ?? 0;
        return {
          success: true,
          data: balance,
          formattedMessage: `Your credit points balance: **${balance}** points`,
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    get_credit_transactions: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const ctFilter: any = {};
        if (params.type) ctFilter.type = params.type as string;
        const result = await creditPointsService.getTransactions(token, userId, ctFilter);
        if (!result.success) throw new Error(result.error || 'Failed to fetch credit transactions');
        const data = result.data as any;
        const transactions = data?.transactions || [];
        return {
          success: true,
          data: transactions,
          formattedMessage: transactions.length > 0
            ? `Credit Transaction History:\n${transactions.map((t: any) => `- ${t.created_at || t.date}: ${t.type || t.name} - ${t.amount > 0 ? '+' : ''}${t.amount} (${t.description || ''})`).join('\n')}`
            : 'No credit transactions found.',
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    get_subscription: async (): Promise<ToolResult> => {
      try {
        const result = await subscriptionService.getUserSubscription(token, userId);
        if (!result.success) throw new Error(result.error || 'Failed to fetch subscription');
        return {
          success: true,
          data: result.data,
          formattedMessage: `Subscription info retrieved.`,
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    get_plans: async (): Promise<ToolResult> => {
      try {
        const result = await subscriptionService.getFeatures();
        if (!result.success) throw new Error(result.error || 'Failed to fetch plans');
        const plans = result.data || [];
        return {
          success: true,
          data: plans,
          formattedMessage: plans.length > 0
            ? `Available Plans:\n${plans.map((p: any) => `- **${p.subscription_name || p.name}**: ${p.subscription_price || p.price} (${p.subscription_duration || p.duration} days)`).join('\n')}`
            : 'No plans available.',
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    get_notifications: async (): Promise<ToolResult> => {
      try {
        const result = await notificationsService.getNotifications(token, userId);
        if (!result.success) throw new Error(result.error || 'Failed to fetch notifications');
        const notifications = result.data || [];
        return {
          success: true,
          data: notifications,
          formattedMessage: notifications.length > 0
            ? `Notifications (${notifications.length}):\n${notifications.map((n: any) => `- ${n.is_read ? '✓' : '○'} ${n.title || n.message}`).join('\n')}`
            : 'No notifications.',
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    get_record_users: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const result = await recordsService.getRecordUsers(token, Number(params.record_id));
        if (!result.success) throw new Error(result.error || 'Failed to fetch record users');
        return { success: true, data: result.data, formattedMessage: `Record users retrieved.` };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    search_entities_by_similarity: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const result = await transactionEntitiesService.findByCosineSimilarity(
          token, userId, params.query as string,
          params.category as any,
          params.limit ? Number(params.limit) : undefined
        );
        if (!result.success) throw new Error(result.error || 'Search failed');
        const matches = result.data || [];
        return {
          success: true,
          data: matches,
          formattedMessage: matches.length > 0
            ? `Semantic search results for "${params.query}":\n${matches.map((m: any) => `- **${m.entity?.transaction_entity_name}** (ID: ${m.entity?.id}, similarity: ${(m.similarity * 100).toFixed(1)}%)`).join('\n')}`
            : `No similar entities found for "${params.query}".`,
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    get_overall_stock_valuation: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const result = await accountsService.fetchOverallStockValuationDetails(token, {
          record_id: Number(params.record_id),
          user_id: userId,
          start_date: params.start_date as string,
          end_date: params.end_date as string,
        } as any);
        if (!result.success) throw new Error(result.error || 'Failed to fetch overall stock valuation');
        return { success: true, data: result.data, formattedMessage: `Overall stock valuation retrieved.` };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    get_overall_trading_account: async (args: string): Promise<ToolResult> => {
      try {
        const params = parseArgs(args);
        const result = await accountsService.fetchOverallTradingAccountDetails(token, {
          record_id: Number(params.record_id),
          user_id: userId,
          start_date: params.start_date as string,
          end_date: params.end_date as string,
        } as any);
        if (!result.success) throw new Error(result.error || 'Failed to fetch overall trading account');
        return { success: true, data: result.data, formattedMessage: `Overall trading account retrieved.` };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },
  };
}

function formatObjectForDisplay(obj: Record<string, unknown> | undefined | null): string {
  if (!obj || Object.keys(obj).length === 0) return '  (none)';
  return Object.entries(obj)
    .map(([key, value]) => {
      if (typeof value === 'object' && value !== null) {
        return `  ${key}:\n${formatObjectForDisplay(value as Record<string, unknown>)}`;
      }
      return `  ${key}: ${value}`;
    })
    .join('\n');
}
